import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { SITE_URL, COMPANY } from "../../config/site";

const DEFAULT_IMAGE = `${SITE_URL}/BG/logopng3D.png`;

function upsertMeta(selector, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([key, value]) => el.setAttribute(key, value));
  return el;
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  return el;
}

/**
 * Per-route document metadata.
 *
 * This is a client-rendered app, so this runs after hydration. Crawlers
 * that execute JavaScript (Googlebot, Bing) read the updated tags;
 * link-preview scrapers that do not execute JS fall back to the static
 * defaults in public/index.html. Both paths now describe DepthLogix
 * rather than Create React App.
 *
 * @param {object}  props
 * @param {string}  props.title        page title (company name is appended)
 * @param {string}  props.description  meta description, ~150-160 chars
 * @param {string}  [props.image]      absolute OG image URL
 * @param {boolean} [props.noIndex]    emit robots noindex
 * @param {object}  [props.jsonLd]     structured data for this page
 */
export default function Seo({
  title,
  description,
  image = DEFAULT_IMAGE,
  noIndex = false,
  jsonLd,
}) {
  const { pathname } = useLocation();

  useEffect(() => {
    const fullTitle = title
      ? `${title} | ${COMPANY.name}`
      : `${COMPANY.name} | ${COMPANY.tagline}`;
    const canonical = `${SITE_URL}${pathname === "/" ? "" : pathname}`;

    document.title = fullTitle;

    if (description) {
      upsertMeta('meta[name="description"]', {
        name: "description",
        content: description,
      });
    }

    upsertMeta('meta[property="og:type"]', { property: "og:type", content: "website" });
    upsertMeta('meta[property="og:site_name"]', { property: "og:site_name", content: COMPANY.name });
    upsertMeta('meta[property="og:title"]', { property: "og:title", content: fullTitle });
    upsertMeta('meta[property="og:url"]', { property: "og:url", content: canonical });
    upsertMeta('meta[property="og:image"]', { property: "og:image", content: image });
    if (description) {
      upsertMeta('meta[property="og:description"]', {
        property: "og:description",
        content: description,
      });
    }

    upsertMeta('meta[name="twitter:card"]', {
      name: "twitter:card",
      content: "summary_large_image",
    });
    upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: fullTitle });
    upsertMeta('meta[name="twitter:image"]', { name: "twitter:image", content: image });
    if (description) {
      upsertMeta('meta[name="twitter:description"]', {
        name: "twitter:description",
        content: description,
      });
    }

    upsertMeta('meta[name="robots"]', {
      name: "robots",
      content: noIndex ? "noindex, nofollow" : "index, follow",
    });

    upsertLink("canonical", canonical);
  }, [title, description, image, noIndex, pathname]);

  // Structured data is written as its own script tag, replaced per route.
  useEffect(() => {
    const ID = "dl-page-jsonld";
    const existing = document.getElementById(ID);
    if (existing) existing.remove();
    if (!jsonLd) return undefined;

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = ID;
    script.textContent = JSON.stringify(jsonLd);
    document.head.appendChild(script);

    return () => {
      const node = document.getElementById(ID);
      if (node) node.remove();
    };
  }, [jsonLd]);

  return null;
}
